import { ArrowLeft, Mail } from "lucide-react";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "../../contexts/LanguageContext";
import authService from "../../services/authService";
import {
  AuthAlert,
  AuthButton,
  AuthField,
  AuthHeader,
  AuthInput,
  AuthLink,
  AuthSecondaryButton,
  AuthStatus,
} from "./AuthPrimitives";

// Função utilitária para validação de email
const validateEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

interface ForgotPasswordFormProps {
  onBackToLogin: () => void;
  onGoToResetPassword?: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onBackToLogin,
  onGoToResetPassword,
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const redirectUri = searchParams.get("redirect_uri");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) setError("");
  };

  const validateForm = (): boolean => {
    if (!email) {
      setError(t("Por favor, insira seu e-mail"));
      return false;
    }

    if (!validateEmail(email)) {
      setError(t("Por favor, insira um e-mail válido"));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setError("");

    try {
      // Chamar o serviço real de reset de senha (normaliza a caixa do e-mail - mesmo
      // motivo do LoginForm)
      const response = await authService.forgotPassword(
        email.trim().toLowerCase(),
        redirectUri,
      );

      if (response.success) {
        setShowSuccess(true);
        console.log(
          "✅ [ForgotPassword] Solicitação de reset enviada para:",
          email,
        );
      } else {
        setError(response.message || t("Erro ao solicitar reset de senha"));
      }
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : t("Erro ao solicitar reset de senha. Tente novamente."),
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Tela de sucesso
  if (showSuccess) {
    return (
      <div className="space-y-6">
        <AuthStatus title={t("Solicitação enviada!")}>
          <p>
            {t("Se o e-mail")}{" "}
            <span className="font-semibold text-slate-700">{email}</span>{" "}
            {t("estiver cadastrado, você receberá um")}{" "}
            <strong>{t("token")}</strong>{" "}
            {t(
              "por e-mail. Copie o token e acesse a tela de redefinição para colar e criar sua nova senha.",
            )}
          </p>
        </AuthStatus>

        <div className="rounded-lg border border-slate-100 bg-slate-50/70 p-3 text-xs text-slate-500">
          <p className="mb-1.5 font-medium text-slate-700">
            {t("Não recebeu o e-mail?")}
          </p>
          <ul className="space-y-1">
            <li>{t("• Verifique sua pasta de spam")}</li>
            <li>{t("• Aguarde alguns minutos")}</li>
            <li>{t("• Confirme se o e-mail está correto")}</li>
            <li>{t("• Tente novamente")}</li>
          </ul>
        </div>

        <div className="space-y-3">
          {onGoToResetPassword && (
            <AuthButton type="button" onClick={onGoToResetPassword}>
              {t("Ir para redefinir senha")}
            </AuthButton>
          )}
          <AuthSecondaryButton type="button" onClick={onBackToLogin}>
            {t("Voltar ao Login")}
          </AuthSecondaryButton>
          <p className="text-center text-sm">
            <AuthLink
              onClick={() => {
                setShowSuccess(false);
                setEmail("");
                setError("");
              }}
            >
              {t("Tentar com outro e-mail")}
            </AuthLink>
          </p>
        </div>
      </div>
    );
  }

  // Formulário de reset
  return (
    <div className="space-y-5">
      <AuthHeader
        title={t("Redefinir Senha")}
        subtitle={t("Informe seu e-mail para receber o link de redefinição")}
      />

      {error && <AuthAlert variant="error" message={error} />}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <AuthField label={t("Endereço de e-mail")} htmlFor="email" required>
          <AuthInput
            type="email"
            id="email"
            name="email"
            icon={Mail}
            value={email}
            onChange={handleChange}
            autoComplete="email"
            placeholder={t("joao.silva@empresa.com")}
            error={!!error}
            required
            autoFocus
          />
        </AuthField>

        <AuthButton
          type="submit"
          loading={isLoading}
          loadingText={t("Enviando...")}
        >
          {t("Enviar link de redefinição")}
        </AuthButton>
      </form>

      <p className="text-center text-sm">
        <AuthLink
          onClick={onBackToLogin}
          className="inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("Voltar ao login")}
        </AuthLink>
      </p>
    </div>
  );
};
